/* Discover empty-state side panel.
 *
 * Renders below "Find your next deal" when no search results yet.
 * Turns dead space into engagement:
 *   - Your Saved Searches → one-click re-run
 *   - Hot Wholesaler Markets → one-tap city pre-fill
 *   - Pro Tip → a single high-value habit for new users
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, Bookmark, Flame, Lightbulb, ArrowRight, MapPin } from "lucide-react";

// Curated wholesaler-friendly markets — picked for cash-buyer depth,
// reasonable comp data, and well-known distress patterns.
const HOT_MARKETS = [
  { city: "Phoenix",      state: "AZ", emoji: "🌵" },
  { city: "Atlanta",      state: "GA", emoji: "🍑" },
  { city: "Dallas",       state: "TX", emoji: "🤠" },
  { city: "Houston",      state: "TX", emoji: "🛢️" },
  { city: "Memphis",      state: "TN", emoji: "🎸" },
  { city: "Indianapolis", state: "IN", emoji: "🏁" },
  { city: "Tampa",        state: "FL", emoji: "🌴" },
  { city: "Miami",        state: "FL", emoji: "🏖️" },
  { city: "Philadelphia", state: "PA", emoji: "🔔" },
  { city: "Cleveland",    state: "OH", emoji: "⚓" },
];

const PRO_TIPS = [
  "Save your buy-box as a search and we'll poll every 1-2 hours for new deals — first call wins.",
  "Click any pin to see ARV, MAO, and a GO/MAYBE/PASS verdict in 2 seconds. No commitment needed.",
  "Wholesalers who run 5+ Discover searches a week close 3× more deals — make it a daily habit.",
  "Reach out within 60 seconds of a NOTICE OF DEFAULT filing. Speed > pitch.",
];


export default function DiscoverInsights({ onPickMarket }) {
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tipIdx] = useState(() => Math.floor(Math.random() * PRO_TIPS.length));

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/saved-searches");
        setSaved(Array.isArray(data) ? data.slice(0, 5) : []);
      } catch {
        /* silent — empty state is fine */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="mt-3 flex flex-col gap-3" data-testid="discover-insights">
      {/* Saved searches — shown only if user has any */}
      {!loading && saved.length > 0 && (
        <div className="card p-3" data-testid="discover-saved-searches">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-bold uppercase tracking-wider text-[var(--ink-2)] flex items-center gap-1">
              <Bookmark size={11} /> Your Saved Searches
            </div>
            <Link to="/v3/alerts" className="text-[10px] text-emerald-700 font-bold hover:underline">
              Manage →
            </Link>
          </div>
          <div className="flex flex-col gap-1.5">
            {saved.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => onPickMarket({
                  city: s.city || "",
                  state: s.state || "",
                  zip: s.zip_code || "",
                  price_min: s.price_min || "",
                  price_max: s.price_max || "",
                  beds_min: s.beds_min || "",
                })}
                className="text-left p-2 -mx-1 rounded hover:bg-stone-50 transition flex items-center gap-2"
                data-testid={`discover-saved-row-${s.id}`}
              >
                <Bell size={12} className="text-[var(--ink-3)] shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">{s.name}</div>
                  <div className="text-[10px] text-[var(--ink-3)] truncate">
                    {[s.city, s.state, s.zip_code].filter(Boolean).join(" · ")}
                    {s.beds_min ? ` · ${s.beds_min}+bd` : ""}
                    {s.price_max ? ` · ≤$${Number(s.price_max).toLocaleString()}` : ""}
                  </div>
                </div>
                <ArrowRight size={12} className="text-[var(--ink-3)]" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Hot markets — always shown */}
      <div className="card p-3" data-testid="discover-hot-markets">
        <div className="text-xs font-bold uppercase tracking-wider text-[var(--ink-2)] flex items-center gap-1 mb-2">
          <Flame size={11} className="text-rose-500" /> Hot Wholesaler Markets
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {HOT_MARKETS.map((m) => (
            <button
              key={`${m.city}-${m.state}`}
              type="button"
              onClick={() => onPickMarket({ city: m.city, state: m.state })}
              className="flex items-center gap-1.5 text-left text-xs font-semibold p-2 rounded border border-[var(--line)] hover:border-emerald-400 hover:bg-emerald-50 transition"
              data-testid={`discover-market-${m.city.toLowerCase()}`}
            >
              <span className="text-base leading-none">{m.emoji}</span>
              <div className="min-w-0">
                <div className="truncate">{m.city}</div>
                <div className="text-[10px] text-[var(--ink-3)] font-normal">{m.state}</div>
              </div>
            </button>
          ))}
        </div>
        <div className="text-[10px] text-[var(--ink-3)] mt-2 leading-tight">
          Markets with deep cash-buyer pools and proven wholesale activity.
        </div>
      </div>

      {/* Pro tip */}
      <div className="rounded-lg p-3 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200"
           data-testid="discover-pro-tip">
        <div className="flex items-start gap-2">
          <Lightbulb size={14} className="text-amber-600 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">
              Pro Tip
            </div>
            <div className="text-xs text-amber-950 leading-snug mt-0.5">
              {PRO_TIPS[tipIdx]}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
